import type { ElementType, HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import {
  CARD_CHROME,
  CARD_CHROME_ACTIVE,
  CARD_CHROME_CONNECTED,
  CARD_CHROME_RAISED,
} from "../primitives/card";

/**
 * FeatureCard: the canonical feature-card anatomy from the connectors
 * design. Circular logo puck, mono eyebrow, serif title, corner state chip,
 * body copy, fact tiles, and a hairline footer band. Surface treatment per
 * state comes from the CARD_CHROME family, so a chrome change flows through
 * every consumer (Connectors provider cards, the signal ledger, Settings).
 *
 * State and the featured scale step ride on data attributes; parts style
 * themselves with group-data variants instead of prop plumbing.
 */
export type FeatureCardState = "default" | "connected" | "active";

const STATE_CHROME: Record<FeatureCardState, string> = {
  default: CARD_CHROME,
  connected: CARD_CHROME_CONNECTED,
  active: CARD_CHROME_ACTIVE,
};

export interface FeatureCardProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  state?: FeatureCardState;
  /** Feature treatment: larger mark, title, copy, and breathing room. */
  featured?: boolean;
  /** Reserves the top-right corner so the head never crowds the chip. */
  hasChip?: boolean;
  /** Hero placement: deeper feature shadow (default state only). */
  raised?: boolean;
  /** Catalog placement: type, mark, and padding step up on wide viewports. */
  fluid?: boolean;
}

export function FeatureCard({
  as,
  state = "default",
  featured = false,
  hasChip = false,
  raised = false,
  fluid = false,
  className,
  children,
  ...rest
}: FeatureCardProps) {
  const Root: ElementType = as ?? "article";
  const chrome = raised && state === "default" ? CARD_CHROME_RAISED : STATE_CHROME[state];
  return (
    <Root
      data-state={state}
      data-featured={featured ? "true" : undefined}
      data-chip={hasChip ? "true" : undefined}
      data-fluid={fluid ? "true" : undefined}
      className={cn(
        "fc group/fc relative flex flex-col p-0",
        chrome,
        "motion-safe:animate-fade-in-up",
        className,
      )}
      {...rest}
    >
      {children}
    </Root>
  );
}

export function FeatureCardBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col gap-[var(--fc-gap)] px-[var(--fc-pad-x)] pt-[var(--fc-pad-t)] pb-[var(--fc-pad-b)]",
        "group-data-[featured=true]/fc:[&>*:last-child]:mt-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function FeatureCardHead({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 group-data-[chip=true]/fc:pr-[96px] [&>div]:flex-1",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Circular white puck holding a brand mark, icon, or monogram. */
export function FeatureCardLogo({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid h-[var(--fc-logo)] w-[var(--fc-logo)] shrink-0 place-items-center rounded-full border border-mist bg-white",
        "font-mono text-sm font-semibold tracking-[0.04em] text-teal-800",
        "shadow-[0_1px_2px_color-mix(in_srgb,var(--color-navy)_6%,transparent),0_3px_8px_-2px_color-mix(in_srgb,var(--color-navy)_8%,transparent)]",
        "group-data-[state=connected]/fc:border-leaf/25",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function FeatureCardId({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  // No intrinsic grow: inside FeatureCardHead (a row) the head supplies it;
  // as a direct body child growing would shove content to the card bottom.
  return <div className={cn("flex min-w-0 flex-col gap-0.5", className)}>{children}</div>;
}

export function FeatureCardEyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "font-mono text-2xs uppercase leading-[1.3] tracking-[0.16em] text-teal-600",
        "group-data-[state=connected]/fc:text-leaf-dark",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function FeatureCardTitle({
  as,
  id,
  children,
  className,
}: {
  as?: "h2" | "h3" | "h4";
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  const Heading = as ?? "h3";
  return (
    <Heading
      id={id}
      className={cn(
        "m-0 font-title text-[length:var(--fc-title)] font-medium leading-[1.2] tracking-[-0.01em] text-navy",
        className,
      )}
    >
      {children}
    </Heading>
  );
}

export type FeatureCardChipTone =
  | "connected"
  | "recommended"
  | "queued"
  | "soon"
  | "neutral"
  | "attention";

const CHIP_TONE: Record<FeatureCardChipTone, string> = {
  connected:
    "border-leaf/35 bg-white/85 text-leaf-dark shadow-[0_1px_2px_color-mix(in_srgb,var(--color-navy)_6%,transparent)] before:h-[5px] before:w-[5px] before:rounded-full before:bg-leaf before:content-['']",
  // Teal rather than the connected leaf: a recommendation is guidance about a
  // card the member has not acted on, not a state they have reached.
  recommended: "border-teal-200 bg-teal-50 text-teal-700",
  queued: "border-attention-timely-border bg-attention-timely-bg text-attention-timely-ink",
  soon: "border-mist-dark bg-gray-50 text-gray-500",
  // gray-600, not gray-500: on the gray-100 field this chip uses, gray-500
  // measures 4.28:1 in the browser, under the 4.5:1 AA floor that its 12px
  // text has to clear. gray-600 on the same field measures 6.27:1. The
  // lighter "soon" field is why that tone gets away with gray-500 (4.64:1).
  neutral: "border-mist-dark bg-gray-100 text-gray-600",
  attention: "border-attention-urgent-border bg-attention-urgent-bg text-attention-urgent-ink",
};

/** Corner state chip. Reserve the corner via FeatureCard's data-chip. */
export function FeatureCardChip({
  tone,
  children,
  className,
}: {
  tone: FeatureCardChipTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "absolute top-[var(--fc-chip-y)] right-[var(--fc-chip-x)] inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] py-1",
        "font-mono text-2xs uppercase tracking-[0.12em]",
        CHIP_TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function FeatureCardDesc({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "m-0 text-[length:var(--fc-desc)] leading-[var(--fc-desc-lh)] text-gray-600",
        "group-data-[featured=true]/fc:max-w-[44ch]",
        className,
      )}
    >
      {children}
    </p>
  );
}

export function FeatureCardTiles({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("flex flex-wrap gap-2.5", className)}>{children}</div>;
}

export function FeatureCardTile({
  label,
  value,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "min-w-0 flex-[1_1_140px] rounded-lg border border-mist bg-white px-[15px] py-[11px] shadow-[0_1px_2px_color-mix(in_srgb,var(--color-navy)_4%,transparent)]",
        "group-data-[state=connected]/fc:border-leaf/25",
        className,
      )}
    >
      <div className="mb-1 font-mono text-2xs uppercase tracking-[0.18em] text-gray-400">
        {label}
      </div>
      <div className="text-sm leading-[1.4] text-navy">{value}</div>
    </div>
  );
}

/** Hairline footer band. Children lay out note-left, action-right. */
export function FeatureCardFoot({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3.5 rounded-b-3xl border-t border-mist bg-gray-50/75 px-[var(--fc-foot-x)] py-[var(--fc-foot-y)]",
        "group-data-[state=connected]/fc:border-leaf/20 group-data-[state=connected]/fc:bg-white/65",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function FeatureCardNote({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "flex flex-1 items-center gap-2 text-sm leading-[1.45] text-gray-500",
        "[&>svg]:shrink-0 [&>svg]:text-teal-700 group-data-[state=connected]/fc:[&>svg]:text-leaf-dark",
        className,
      )}
    >
      {children}
    </span>
  );
}
